// src/Dashboard/admin/sections/AdminsSection.jsx
import { useEffect, useMemo, useState } from "react";
import { RefreshCw, Plus, Trash2 } from "lucide-react";

import { listAdmins, createScopedAdmin, deleteAdmin } from "../../../services/adminApi";
import { ModalShell, SelectField, Badge } from "../components/ui";

/* ------------------------------ safe helpers ------------------------------ */
function safeId(x) {
  if (x === undefined || x === null) return "";
  return String(x);
}

function getIsActive(row) {
  if (row?.isactive !== undefined) return String(row.isactive) === "1" ? "1" : "0";
  if (typeof row?.is_active === "boolean") return row.is_active ? "1" : "0";
  if (row?.is_active === 1 || row?.is_active === "1") return "1";
  const s = String(row?.account_status || "").toLowerCase();
  if (s === "inactive") return "0";
  return "1";
}

function getConferenceId(x) {
  return safeId(x?.id ?? x?.conference_id ?? x?.conferenceId);
}
function getDistrictId(x) {
  return safeId(x?.id ?? x?.district_id ?? x?.districtId);
}

export default function AdminsSection({ getToken, hierarchy = { conferences: [], districts: [] } }) {
  const conferences = hierarchy?.conferences || [];
  const districts = hierarchy?.districts || [];

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  // create modal
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [level, setLevel] = useState("conference"); // conference | district
  const [conferenceId, setConferenceId] = useState("");
  const [districtId, setDistrictId] = useState("");

  async function withToken(fn) {
    const token = await getToken?.();
    if (!token) throw new Error("Missing auth token");
    return fn(token);
  }

  async function load() {
    setLoading(true);
    setErr("");
    try {
      await withToken(async (token) => {
        const res = await listAdmins(token);
        const list = Array.isArray(res) ? res : res?.data || [];
        setRows(list);
      });
    } catch (e) {
      setErr(e?.message || "Failed to load admins");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const query = String(q || "").trim().toLowerCase();
    if (!query) return rows || [];
    return (rows || []).filter((a) => {
      const name = String(a?.full_name || a?.fullName || "").toLowerCase();
      const email = String(a?.email || "").toLowerCase();
      const role = String(a?.role || "").toLowerCase();
      return name.includes(query) || email.includes(query) || role.includes(query);
    });
  }, [rows, q]);

  const availableDistricts = useMemo(() => {
    if (!conferenceId) return [];
    return (districts || []).filter(
      (d) =>
        String(d.station_id ?? d.stationId ?? d.conf_id ?? d.conference_id ?? d.conferenceId) || true // best effort
    );
    // NOTE: If your districts table has conference_id directly, we can tighten this later.
  }, [districts, conferenceId]);

  async function onDelete(row) {
    const id = safeId(row?.id);
    if (!id) return;
    const ok = window.confirm("Delete this admin? This cannot be undone.");
    if (!ok) return;

    try {
      setLoading(true);
      await withToken((token) => deleteAdmin(token, id));
      await load();
    } catch (e) {
      setErr(e?.message || "Failed to delete admin");
    } finally {
      setLoading(false);
    }
  }

  function resetCreate() {
    setEmail("");
    setFullName("");
    setLevel("conference");
    setConferenceId("");
    setDistrictId("");
    setCreateErr("");
  }

  async function onCreate() {
    setCreateErr("");
    const e = String(email || "").trim();
    if (!e) {
      setCreateErr("Email is required.");
      return;
    }
    if (level === "conference" && !conferenceId) {
      setCreateErr("Pick a conference.");
      return;
    }
    if (level === "district" && (!conferenceId || !districtId)) {
      setCreateErr("Pick a conference and district.");
      return;
    }

    try {
      setCreating(true);
      await withToken(async (token) => {
        const payload = {
          email: e,
          fullName: String(fullName || "").trim() || undefined,
          level,
          conferenceId: conferenceId || undefined,
          districtId: level === "district" ? districtId || undefined : undefined,
        };
        await createScopedAdmin(token, payload);
      });

      setOpen(false);
      resetCreate();
      await load();
    } catch (err) {
      setCreateErr(err?.message || "Failed to create admin");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-lg font-semibold">Admins</div>
            <div className="text-xs text-slate-500">
              Union/Conference/District admins (server enforces who can create/delete).
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>

            <button
              type="button"
              onClick={() => {
                resetCreate();
                setOpen(true);
              }}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 text-sm font-semibold"
            >
              <Plus size={16} />
              New admin
            </button>
          </div>
        </div>

        {err ? (
          <div className="mt-4 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-100 dark:border-amber-800">
            {err}
          </div>
        ) : null}

        <div className="mt-4 flex items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name/email/role…"
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
          />
        </div>

        <div className="mt-4 overflow-auto rounded-xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/70 text-slate-700 dark:text-slate-200">
              <tr>
                <th className="p-3 text-left">Admin</th>
                <th className="p-3 text-left">Role</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(filtered || []).map((a) => {
                const id = safeId(a?.id);
                const name = a?.full_name || a?.fullName || "—";
                const email = a?.email || "—";
                const role = a?.role || "admin";
                const active = getIsActive(a) !== "0";

                return (
                  <tr
                    key={id || email}
                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <td className="p-3">
                      <div className="font-semibold">{name}</div>
                      <div className="text-xs text-slate-500">{email}</div>
                    </td>
                    <td className="p-3">
                      <span className="font-semibold">{role}</span>
                    </td>
                    <td className="p-3 text-center">
                      <Badge tone={active ? "success" : "neutral"} label={active ? "Active" : "Inactive"} />
                    </td>
                    <td className="p-3 text-right">
                      <button
                        type="button"
                        onClick={() => onDelete(a)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 text-xs"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}

              {!loading && (filtered || []).length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-slate-500">
                    No admins found.
                  </td>
                </tr>
              ) : null}

              {loading ? (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {open ? (
        <ModalShell
          title="Create Scoped Admin"
          subtitle="Invite a new admin (scope enforced server-side)"
          onClose={() => setOpen(false)}
          footer={
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onCreate}
                disabled={creating}
                className="px-4 py-2 rounded-xl bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold disabled:opacity-60"
              >
                {creating ? "Creating…" : "Create admin"}
              </button>
            </div>
          }
        >
          {createErr ? (
            <div className="mb-3 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-100 dark:border-amber-800">
              {createErr}
            </div>
          ) : null}

          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
                Email
              </label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
                placeholder="admin@example.com"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
                Full name (optional)
              </label>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
                placeholder="Jane Doe"
              />
            </div>

            <SelectField
              label="Level"
              value={level}
              onChange={(v) => {
                setLevel(v);
                setConferenceId("");
                setDistrictId("");
              }}
              options={[
                { value: "conference", label: "Conference admin" },
                { value: "district", label: "District admin" },
              ]}
            />

            <SelectField
              label="Conference"
              value={conferenceId}
              onChange={(v) => {
                setConferenceId(v);
                setDistrictId("");
              }}
              options={[
                { value: "", label: "Select conference…" },
                ...(conferences || [])
                  .filter((c) => c.isactive !== "0")
                  .map((c) => ({ value: String(getConferenceId(c)), label: c.name })),
              ]}
            />

            {level === "district" ? (
              <SelectField
                label="District"
                value={districtId}
                onChange={setDistrictId}
                options={[
                  { value: "", label: "Select district…" },
                  ...(availableDistricts || []).map((d) => ({
                    value: String(getDistrictId(d)),
                    label: d.name,
                  })),
                ]}
                disabled={!conferenceId}
              />
            ) : (
              <div className="text-xs text-slate-500 flex items-end">
                District is only required for District admins.
              </div>
            )}
          </div>
        </ModalShell>
      ) : null}
    </div>
  );
}
